'use client';

import { LanguageSwitcher, useTranslation } from '@/features/i18n';
import { setNavbarLogo } from '@/features/navigation/store/logoStore';
import { SearchButton } from '@/features/search/';
import { ThemeSwitch } from '@/features/theme';
import { WatchListButton } from '@/features/watchlist';
import { Link } from '@/ui/Link';
import { Button } from '@/ui/buttons/Button';
import { TVOD_ENABLED } from '@/util/constants';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  PowerIcon,
  StarIcon,
  UserIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  useActiveSubscriptions,
  useAuthentication,
} from '@vimond/features/authentication';
import { useSubprofiles } from '@vimond/features/subprofiles';
import { useLocale } from '@vimond/i18n/hooks/useLocale';
import {
  MenuItemType,
  MenuType,
  PageType,
} from '@vimond/services/content-discovery';
import { cn } from '@vimond/theme/tailwind';
import { getEnvVar } from '@vimond/utils/env';
import Image from 'next/image';
import { Fragment, Suspense, useEffect, useRef } from 'react';

const PAYMENTS_ENABLED = getEnvVar('NEXT_PUBLIC_PAYMENTS_ENABLED') !== 'false';

type NavBarProps = {
  authenticationEnabled: boolean;
  pages: readonly PageType[];
  menu?: MenuType;
};

export const NavBar = ({
  authenticationEnabled,
  menu: globalMenu,
  pages,
}: NavBarProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);

  // get the current page path without the locale
  const { pathnameWithoutLocale: pathname } = useLocale();
  const { activeSubprofile, setActiveSubprofile, subprofiles } =
    useSubprofiles();
  const { isLoggedIn, signOut } = useAuthentication();

  const { activeSubscriptions, allAgreementsAreInactive, loading } =
    useActiveSubscriptions();

  useEffect(() => {
    const handleScroll = () => {
      const nav = ref.current;
      if (nav) {
        if (window.scrollY > 0) {
          nav.classList.add('to-black/50');
          nav.classList.remove('to-transparent');
        } else {
          nav.classList.add('to-transparent');
          nav.classList.remove('to-black/50');
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // does the page have a header menu that should override the global menu?
  const getLogoAndMenu = (pages: readonly PageType[]) => {
    const page = pages?.find(
      (page) =>
        page['page-slug'] === pathname ||
        (pathname === '/browse' && page['page-slug'] === '/'),
    );
    const pageHeader = page?.elements?.find(
      (element) =>
        element.contentType === 'menu' &&
        element['layout-menu-position'] === 'Header',
    ) as MenuType;

    const findLogoAndMenues = (menu: MenuType) => {
      const logo = menu.elements?.[0]?.images?.defaultUrl ?? '';
      setNavbarLogo(logo);
      const logoRedirectUrl = menu.elements?.[0]?.url;

      // pick all menu items that are not the first one (the logo)
      const centerMenu =
        (menu.elements
          ?.slice(1)
          .filter((m) => m.contentType === 'menu_item') as MenuItemType[]) ||
        [];

      // pick any submenus that are in the menu and assume they are call to action menus
      const callToActionMenu =
        (menu.elements
          ?.filter((m) => m.contentType === 'menu')
          .map((m) => m.elements)
          .flat() as MenuItemType[]) || [];
      return { logo, logoRedirectUrl, centerMenu, callToActionMenu };
    };

    if (pageHeader) {
      // override the global menu with the page header menu
      return findLogoAndMenues(pageHeader);
    } else if (globalMenu) {
      // use the global menu
      return findLogoAndMenues(globalMenu);
    }
    return {
      logo: '',
      logoRedirectUrl: '/',
      centerMenu: [],
      callToActionMenu: [],
    };
  };
  const { callToActionMenu, centerMenu, logo, logoRedirectUrl } =
    getLogoAndMenu(pages);

  return (
    <header className="sticky top-0 z-50">
      <Disclosure
        as="nav"
        ref={ref}
        className="z-[9999] w-full bg-black transition duration-100 ease-out"
      >
        {({ close, open }) => (
          <>
            <div className="px-2 mx-auto md:px-6 xl:px-14">
              <div className="relative flex items-center justify-between h-16 xl:h-20">
                <div className="absolute inset-y-0 left-0 flex items-center xl:hidden">
                  {/* Mobile menu button*/}
                  <Disclosure.Button className="relative inline-flex items-center justify-center p-2 text-gray-400 rounded-md hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                    <span className="absolute -inset-0.5" />
                    <span className="sr-only">{t('open-main-menu')}</span>
                    {open ? (
                      <XMarkIcon className="block w-6 h-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block w-6 h-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
                <div className="flex items-center justify-center flex-1 xl:items-stretch xl:justify-start">
                  <div className="flex items-center flex-shrink-0">
                    <Link href={logoRedirectUrl ?? '/'} aria-label="Logo">
                      <Image
                        src={logo}
                        alt=""
                        priority
                        width={160}
                        height={32}
                        className="w-auto o1bject-contain o1bject-left max-h-10"
                      />
                    </Link>
                  </div>
                  <div className="justify-center hidden gap-3 xl:flex xl:flex-1 xl:items-center xl:gap-6 xl:ml-8 xl:gap-8">
                    {centerMenu.map((item) => (
                      <Link
                        key={item.title}
                        href={(item.url === pathname ? '' : item.url) ?? ''}
                        className={cn(
                          'text-shadow group relative text-sm font-bold text-white transition-all xl:text-lg',
                          {
                            'opacity-70 hover:opacity-100':
                              item.url !== pathname,
                          },
                        )}
                        aria-current={
                          item.url === pathname ? 'page' : undefined
                        }
                      >
                        {item.title}
                        <span className="bg-text-highlight absolute bottom-0 left-1/2 right-1/2 h-0.5 max-w-0 transition-all duration-500 group-hover:left-0 group-hover:right-0 group-hover:max-w-full"></span>
                      </Link>
                    ))}
                  </div>
                </div>
                <div className="absolute inset-y-0 right-0 flex items-center gap-3 pr-0 md:static md:inset-auto md:ml-6 md:pr-2">
                  <div className="flex items-center gap-3">
                    {/* TODO: remove this when we have support for both TVOD and SVOD together */}
                    {!TVOD_ENABLED &&
                      PAYMENTS_ENABLED &&
                      (activeSubscriptions?.length === 0 ||
                        allAgreementsAreInactive) &&
                      !loading &&
                      isLoggedIn && (
                        <Link href="/subscribe">
                          <Button
                            variant={'primary'}
                            size={'sm'}
                            className="flex items-center gap-2"
                          >
                            <StarIcon className="h-8 w-8" />
                            {t('subscriptions.subscribe')}
                          </Button>
                        </Link>
                      )}
                    <SearchButton active={pathname === '/search'} />
                    {authenticationEnabled && (
                      <WatchListButton
                        active={pathname === '/browse/watchlist'}
                      />
                    )}
                  </div>
                  <div className="items-center hidden gap-3 xl:flex">
                    <Suspense>
                      <ThemeSwitch color="white" />
                    </Suspense>
                    <Suspense>
                      <LanguageSwitcher color="white" />
                    </Suspense>
                  </div>

                  {!activeSubprofile && authenticationEnabled && (
                    <div className="hidden md:block">
                      <Link href="/auth/signin">
                        <Button variant="text" size="sm">
                          {t('sign-in')}
                        </Button>
                      </Link>
                      <Link href="/auth/signup">
                        <Button
                          variant={
                            callToActionMenu.length > 0 ? 'text' : 'primary'
                          }
                          size="sm"
                        >
                          {t('sign-up')}
                        </Button>
                      </Link>
                    </div>
                  )}
                  {activeSubprofile && authenticationEnabled && (
                    <Menu as="div" className="relative">
                      <div>
                        <Menu.Button className="relative flex text-sm bg-gray-800 rounded-md focus:outline-none">
                          <span className="absolute -inset-1.5" />
                          <span className="sr-only">{t('open-user-menu')}</span>
                          <div className="flex flex-col gap-1 my-0 text-center group">
                            <div
                              className="relative flex items-center justify-center w-8 h-8 mx-auto text-lg font-bold text-white border-4 border-transparent rounded-md bg-slate-500"
                              style={{
                                backgroundColor:
                                  activeSubprofile.properties?.color,
                              }}
                            >
                              {activeSubprofile.subProfileName?.[0]?.toUpperCase()}
                            </div>
                          </div>
                        </Menu.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 z-10 w-48 py-1 mt-2 origin-top-right rounded-md shadow-lg bg-background-primary ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <>
                            {subprofiles
                              ?.filter(
                                (profile) =>
                                  profile.subProfileId !==
                                  activeSubprofile?.subProfileId,
                              )
                              .map((profile) => (
                                <Menu.Item key={profile.subProfileId}>
                                  <div
                                    onClick={async () => {
                                      await setActiveSubprofile(profile);
                                    }}
                                    className="block px-4 py-2 text-sm cursor-pointer text-text-primary hover:bg-background-secondary"
                                  >
                                    <div className="flex items-center gap-2 capitalize">
                                      <div className="flex flex-col gap-1 my-0 text-center">
                                        <div
                                          className="relative flex items-center justify-center w-8 h-8 mx-auto text-lg font-bold border-4 border-transparent rounded-md text-text-primary"
                                          style={{
                                            backgroundColor:
                                              profile?.properties?.color,
                                          }}
                                        >
                                          {profile?.subProfileName?.[0]}
                                        </div>
                                      </div>

                                      {profile.subProfileName}
                                    </div>
                                  </div>
                                </Menu.Item>
                              ))}

                            <Menu.Item>
                              <Link href="/profiles/manage">
                                <div className="block px-4 py-2 text-sm hover:bg-background-secondary">
                                  <div className="flex items-center gap-2">
                                    <UsersIcon className="w-8 h-8 border-4 border-transparent rounded-md" />
                                    {t('profile.manage-profiles')}
                                  </div>
                                </div>
                              </Link>
                            </Menu.Item>
                            <Menu.Item>
                              <Link href="/account">
                                <div className="block px-4 py-2 text-sm hover:bg-background-secondary">
                                  <div className="flex items-center gap-2">
                                    <UserIcon className="w-8 h-8 border-4 border-transparent rounded-md" />
                                    {t('account.manage-your-account')}
                                  </div>
                                </div>
                              </Link>
                            </Menu.Item>
                            <Menu.Item>
                              <div
                                onClick={() => signOut()}
                                className="cursor-pointer"
                              >
                                <div className="block px-4 py-2 text-sm hover:bg-background-secondary">
                                  <div className="flex items-center gap-2">
                                    <PowerIcon className="w-8 h-8 border-4 border-transparent rounded-md" />
                                    {t('sign-out')}
                                  </div>
                                </div>
                              </div>
                            </Menu.Item>
                          </>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  )}
                  <div className="hidden md:block">
                    {callToActionMenu.map((item) => (
                      <Link href={item.url ?? ''} key={item.id}>
                        <Button variant="primary" size="sm">
                          {item.title}
                        </Button>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Disclosure.Panel className="xl:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 bg-black">
                <div className="mb-2 flex justify-end">
                  <LanguageSwitcher />
                </div>
                {centerMenu.map((item) => (
                  <Link
                    key={item.title}
                    href={(item.url === pathname ? '' : item.url) ?? ''}
                    onClick={() => close()}
                    className={cn(
                      'block rounded-md px-3 py-2 text-base font-medium',
                      {
                        'bg-gray-900 text-white': item.url === pathname,
                        'text-gray-300 hover:bg-gray-700 hover:text-white':
                          item.url !== pathname,
                      },
                    )}
                    aria-current={item.url === pathname ? 'page' : undefined}
                  >
                    {item.title}
                  </Link>
                ))}
                {callToActionMenu.map((item) => {
                  return (
                    <Link
                      key={item.title}
                      href={item.url ?? '#'}
                      onClick={() => close()}
                      className={`bg-button-primary-background block rounded-md px-3 py-2 text-center text-base font-medium hover:bg-opacity-70 hover:text-white`}
                      aria-current={item.url === pathname ? 'page' : undefined}
                    >
                      {item.title}
                    </Link>
                  );
                })}
                {!activeSubprofile && authenticationEnabled && (
                  <>
                    <Link
                      href="/auth/signin"
                      onClick={() => close()}
                      className={`block rounded-md px-3 py-2 text-center text-base font-medium text-white hover:bg-gray-700 hover:text-white`}
                    >
                      {t('sign-in')}
                    </Link>

                    <Link
                      href="/auth/signup"
                      onClick={() => close()}
                      className={`bg-button-primary-background block rounded-md px-3 py-2 text-center text-base font-medium text-white hover:bg-opacity-70 hover:text-white`}
                    >
                      {t('sign-up')}
                    </Link>
                  </>
                )}
                {activeSubprofile && authenticationEnabled && (
                  <>
                    <Link
                      href="/browse/watchlist"
                      onClick={() => close()}
                      className={`block rounded-md px-3 py-2 text-base font-medium text-white hover:bg-gray-700 hover:text-white`}
                    >
                      {t('watchlist')}
                    </Link>

                    <Link
                      href=""
                      onClick={() => signOut()}
                      className={`block rounded-md px-3 py-2 text-base font-medium text-white hover:bg-gray-700 hover:text-white`}
                    >
                      {t('sign-out')}
                    </Link>
                  </>
                )}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </header>
  );
};
